import React, { useState, useEffect } from 'react';
import { X, UserPlus, CheckCircle, XCircle, Trash2, AlertTriangle } from 'lucide-react';
import { usersAPI } from '../../../services/api';
import { toast } from 'sonner';

const ApprovalRequestModal = ({ 
  isOpen, 
  onClose, 
  actionType, 
  selectedUser, 
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    actionType: '',
    userId: '',
    userData: {}
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && actionType && selectedUser) {
      setFormData({
        actionType,
        userId: selectedUser._id,
        userData: selectedUser
      });
    }
  }, [isOpen, actionType, selectedUser]);

  const getActionIcon = (type) => {
    switch (type) {
      case 'create':
        return <UserPlus className="h-5 w-5 text-blue-600" />;
      case 'activate':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'deactivate':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'delete':
        return <Trash2 className="h-5 w-5 text-red-600" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
    }
  };

  const getActionTitle = (type) => {
    switch (type) {
      case 'create':
        return 'Create User';
      case 'activate':
        return 'Activate User';
      case 'deactivate':
        return 'Deactivate User';
      case 'delete':
        return 'Delete User';
      default:
        return 'User Action';
    }
  };

  const getActionDescription = (type, user) => {
    if (type === 'create') {
      return 'Submit request to create a new user';
    }
    
    const userName = user?.personalInfo?.firstName && user?.personalInfo?.lastName 
      ? `${user.personalInfo.firstName} ${user.personalInfo.lastName}`
      : user?.email || 'User';
    
    switch (type) {
      case 'activate':
        return `Submit request to activate ${userName}`;
      case 'deactivate':
        return `Submit request to deactivate ${userName}`;
      case 'delete':
        return `Submit request to delete ${userName}`;
      default:
        return `Submit request for ${userName}`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    try {
      const requestData = {
        actionType: formData.actionType,
        userId: formData.userId,
        userData: formData.userData
      };

      await usersAPI.submitApprovalRequest(requestData);
      
      toast.success('Approval request submitted successfully');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Submit approval request error:', error);
      toast.error(error.response?.data?.message || 'Failed to submit approval request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        actionType: '',
        userId: '',
        userData: {}
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {getActionIcon(formData.actionType)}
            <h2 className="text-lg font-semibold text-gray-900">
              {getActionTitle(formData.actionType)}
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            {getActionDescription(formData.actionType, formData.userData)}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary"
              >
                {isSubmitting ? 'Submitting...' : 'Submit for Approval'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApprovalRequestModal;
